import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, Users2, Scale, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const PILLARS = [
  {
    key: "ENVIRONMENTAL", letter: "E", title: "Environmental", icon: Leaf, link: "/environmental",
    colors: "bg-green-50 border-green-200", iconColor: "text-green-700",
    desc: "Energia, emissioni, inquinamento, biodiversità, acqua, rifiuti. Disclosure VSME B3–B7 e C3–C4.",
    testid: "esg-pillar-environmental",
  },
  {
    key: "SOCIAL", letter: "S", title: "Social", icon: Users2, link: "/social",
    colors: "bg-sky-50 border-sky-200", iconColor: "text-sky-700",
    desc: "Forza lavoro, salute e sicurezza, retribuzioni, formazione, diritti umani. Disclosure VSME B8–B10 e C5–C7.",
    testid: "esg-pillar-social",
  },
  {
    key: "GOVERNANCE", letter: "G", title: "Governance", icon: Scale, link: null,
    colors: "bg-indigo-50 border-indigo-200", iconColor: "text-indigo-700",
    desc: "Anticorruzione, attività escluse, diversità negli organi di governance. Disclosure VSME B11, C8 e C9.",
    testid: "esg-pillar-governance",
  },
];

export default function EsgPillars() {
  const { companyId, period } = useTenant();
  const [overview, setOverview] = useState(null);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/vsme`);
      setOverview(data);
    } catch {
      setOverview(null);
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="esg-pillars-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Il mio ESG</h1>
          <p className="text-sm text-slate-600 mt-1">
            I tre pilastri della sostenibilità — esercizio {period?.year}. Dati reali dal VSME Engine.
          </p>
        </div>
        <Link to="/questionario" data-testid="goto-questionnaire-from-esg">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-green-700 hover:bg-green-800 rounded-md px-4 py-2 transition-colors">
            Completa con il questionario <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </span>
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PILLARS.map((p) => {
          const stats = overview?.categories?.[p.key];
          return (
            <Card key={p.key} className={`border ${p.colors}`} data-testid={p.testid}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="w-9 h-9 rounded-md bg-white/80 border border-slate-200 flex items-center justify-center">
                    <p.icon className={`w-5 h-5 ${p.iconColor}`} aria-hidden="true" />
                  </span>
                  {p.letter} — {p.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-700 leading-relaxed">{p.desc}</p>
                {stats ? (
                  <div data-testid={`${p.testid}-stats`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="flex-1 h-2.5 rounded-full bg-white/70 border border-slate-200">
                        <div className="h-full rounded-full bg-green-700" style={{ width: `${stats.completion}%` }} />
                      </div>
                      <span className="text-sm font-bold" data-testid={`${p.testid}-completion`}>{stats.completion}%</span>
                    </div>
                    <p className="text-xs text-slate-700">
                      {stats.completed} disponibili · {stats.missing} mancanti · {stats.to_verify} da verificare · {stats.not_applicable} non applicabili
                    </p>
                    {p.link && (
                      <Link to={p.link} className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline" data-testid={`${p.testid}-link`}>
                        Apri il motore {p.title} <ArrowRight className="w-3 h-3" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                ) : (
                  <Badge variant="outline" className="bg-white/70 text-slate-600 border-slate-300" data-testid={`${p.testid}-badge`}>
                    Dati non ancora disponibili
                  </Badge>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
